/**
 * OrganizationDashboard - Dashboard view showing organization-wide metrics
 *
 * Displays:
 * - Quick insight cards with key metrics (PR throughput, features, bugfixes)
 * - Data table with detailed breakdown
 * - Stacked bar charts showing commit distribution by classification and size
 */

import {
  ActivityHeatmap,
  ActivityListCard,
  DivergingBarChart,
  InsightDashboard,
  InsightDashboardStore,
  MetricsCard,
  PageHeader,
  SimpleBarChart,
  StackedBarChart,
  type TabDefinition,
  TabsWithStore,
} from "@getdevpulse/shared-ui"
import { GitCommit, GitMerge, GitPullRequest, Tag } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useMemo } from "react"
import { logger } from "../lib/logger"
import { useDataStore, useI18nStore } from "../stores/StoreContext"
import type { ComplexityClass } from "../types/data"

export const OrganizationDashboard = observer(() => {
  const dataStore = useDataStore()
  const i18nStore = useI18nStore()

  // Create the dashboard store with computed data
  const dashboardStore = useMemo(() => {
    const commits = dataStore.allCommits
    const prs = dataStore.allPullRequests

    // Calculate date range
    const now = new Date()
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)

    // Helper function to generate daily chart data
    const generateDailyChartData = (
      items: Array<{ timestampDate?: Date; createdAtDate?: Date }>,
      startDate: Date,
      endDate: Date,
    ) => {
      const dailyCounts = new Map<string, number>()
      const start = new Date(startDate)
      start.setHours(0, 0, 0, 0)
      const end = new Date(endDate)
      end.setHours(23, 59, 59, 999)

      // Initialize all days with 0
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyCounts.set(dateKey, 0)
      }

      // Count items per day
      items.forEach((item) => {
        const date = item.timestampDate || item.createdAtDate
        if (date && date >= start && date <= end) {
          const dateKey = date.toISOString().split("T")[0]
          dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
        }
      })

      return Array.from(dailyCounts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, value]) => ({ value }))
    }

    // Current week metrics
    const currentWeekPRs = prs.filter(
      (pr) => pr.createdAtDate >= oneWeekAgo && pr.state === "merged",
    )
    const currentWeekFeatures = commits.filter(
      (c) => c.timestampDate >= oneWeekAgo && c.commitType === "feature",
    )
    const currentWeekBugfixes = commits.filter(
      (c) => c.timestampDate >= oneWeekAgo && c.commitType === "bugfix",
    )
    const currentWeekMaintenance = commits.filter(
      (c) =>
        c.timestampDate >= oneWeekAgo &&
        c.commitType &&
        c.commitType !== "feature" &&
        c.commitType !== "bugfix",
    )

    // Previous week metrics (for comparison)
    const previousWeekPRs = prs.filter(
      (pr) =>
        pr.createdAtDate >= twoWeeksAgo && pr.createdAtDate < oneWeekAgo && pr.state === "merged",
    )
    const previousWeekFeatures = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo &&
        c.timestampDate < oneWeekAgo &&
        c.commitType === "feature",
    )
    const previousWeekBugfixes = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo && c.timestampDate < oneWeekAgo && c.commitType === "bugfix",
    )
    const previousWeekMaintenance = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo &&
        c.timestampDate < oneWeekAgo &&
        c.commitType &&
        c.commitType !== "feature" &&
        c.commitType !== "bugfix",
    )

    logger.debug("Calculated organization metrics", {
      component: "OrganizationDashboard",
      currentWeekPRs: currentWeekPRs.length,
      currentWeekFeatures: currentWeekFeatures.length,
      currentWeekBugfixes: currentWeekBugfixes.length,
      currentWeekMaintenance: currentWeekMaintenance.length,
    })

    // Create insights with their associated table data
    const insights = [
      {
        id: "pr-throughput",
        insight: {
          id: "pr-throughput",
          subtitle: i18nStore.t("insightMergedPRs"),
          date: i18nStore.t("quickInsightDate"),
          title: i18nStore.t("insightPRThroughput"),
          value: currentWeekPRs.length.toString(),
          secondaryText: i18nStore.tf("quickInsightVsLastWeek", {
            count: previousWeekPRs.length.toString(),
          }),
          chartData: generateDailyChartData(currentWeekPRs, oneWeekAgo, now),
        },
        tableData: currentWeekPRs.map((pr) => ({
          title: pr.title,
          repoName: pr.repoName,
          url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
          author: pr.creatorHandle,
          date: pr.createdAtDate.toLocaleDateString(),
          additions: pr.additions?.toString() || "0",
          deletions: pr.deletions?.toString() || "0",
          files: pr.changedFiles?.toString() || "0",
        })),
        tableColumns: [
          {
            accessorKey: "title",
            header: i18nStore.t("tableColumnTitle"),
            cell: ({
              row,
            }: {
              row: {
                original: { url: string; repoName: string }
                getValue: (key: string) => string
              }
            }) => (
              <div className="flex flex-col gap-0.5">
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("title")}
                </a>
                <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
              </div>
            ),
          },
          { accessorKey: "author", header: i18nStore.t("tableColumnAuthor") },
          {
            accessorKey: "date",
            header: i18nStore.t("tableColumnCreated"),
          },
          {
            accessorKey: "additions",
            header: i18nStore.t("tableColumnAdditions"),
          },
          {
            accessorKey: "deletions",
            header: i18nStore.t("tableColumnDeletions"),
          },
          { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
        ],
      },
      {
        id: "features",
        insight: {
          id: "features",
          subtitle: i18nStore.t("insightFeatureCommits"),
          date: i18nStore.t("quickInsightDate"),
          title: i18nStore.t("insightFeatures"),
          value: currentWeekFeatures.length.toString(),
          secondaryText: i18nStore.tf("quickInsightVsLastWeek", {
            count: previousWeekFeatures.length.toString(),
          }),
          chartData: generateDailyChartData(currentWeekFeatures, oneWeekAgo, now),
        },
        tableData: currentWeekFeatures.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          repoName: commit.repoName,
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: i18nStore.t("tableColumnMessage"),
            cell: ({
              row,
            }: {
              row: {
                original: { url: string; repoName: string }
                getValue: (key: string) => string
              }
            }) => (
              <div className="flex flex-col gap-0.5">
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
                <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
              </div>
            ),
          },
          { accessorKey: "author", header: i18nStore.t("tableColumnAuthor") },
          { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
          { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
          { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
        ],
      },
      {
        id: "bugfixes",
        insight: {
          id: "bugfixes",
          subtitle: i18nStore.t("insightBugfixCommits"),
          date: i18nStore.t("quickInsightDate"),
          title: i18nStore.t("insightBugfixes"),
          value: currentWeekBugfixes.length.toString(),
          secondaryText: i18nStore.tf("quickInsightVsLastWeek", {
            count: previousWeekBugfixes.length.toString(),
          }),
          chartData: generateDailyChartData(currentWeekBugfixes, oneWeekAgo, now),
        },
        tableData: currentWeekBugfixes.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          repoName: commit.repoName,
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: i18nStore.t("tableColumnMessage"),
            cell: ({
              row,
            }: {
              row: {
                original: { url: string; repoName: string }
                getValue: (key: string) => string
              }
            }) => (
              <div className="flex flex-col gap-0.5">
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
                <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
              </div>
            ),
          },
          { accessorKey: "author", header: i18nStore.t("tableColumnAuthor") },
          { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
          { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
          { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
        ],
      },
      {
        id: "maintenance",
        insight: {
          id: "maintenance",
          subtitle: i18nStore.t("insightMaintenanceCommits"),
          date: i18nStore.t("quickInsightDate"),
          title: i18nStore.t("insightMaintenance"),
          value: currentWeekMaintenance.length.toString(),
          secondaryText: i18nStore.tf("quickInsightVsLastWeek", {
            count: previousWeekMaintenance.length.toString(),
          }),
          chartData: generateDailyChartData(currentWeekMaintenance, oneWeekAgo, now),
        },
        tableData: currentWeekMaintenance.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          repoName: commit.repoName,
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          type: commit.commitType || "other",
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: i18nStore.t("tableColumnMessage"),
            cell: ({
              row,
            }: {
              row: {
                original: { url: string; repoName: string }
                getValue: (key: string) => string
              }
            }) => (
              <div className="flex flex-col gap-0.5">
                <a
                  href={row.original.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {row.getValue("message")}
                </a>
                <span className="text-xs text-muted-foreground">{row.original.repoName}</span>
              </div>
            ),
          },
          { accessorKey: "author", header: i18nStore.t("tableColumnAuthor") },
          { accessorKey: "type", header: "Type" },
          { accessorKey: "date", header: i18nStore.t("tableColumnDate") },
          { accessorKey: "score", header: i18nStore.t("tableColumnScore") },
          { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
        ],
      },
    ]

    return InsightDashboardStore.create({
      insights,
      selectedInsightId: "pr-throughput",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale needed for translation reactivity
  }, [dataStore.allCommits, dataStore.allPullRequests, i18nStore.locale])

  // Compute bar chart data for commits (all commits, not filtered by week)
  const barChartData = useMemo(() => {
    const commits = dataStore.allCommits

    // Define the order of complexity classes and commit types
    const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
    const commitTypeOrder = [
      "feature",
      "bugfix",
      "refactoring",
      "ci",
      "tests",
      "docs",
      "chore",
      "perf",
      "revert",
      "merge",
      "archive",
      "styling", // Renamed from "style" to avoid React reserved prop conflict
      "other",
    ]

    // Map commit types to avoid reserved words
    // "style" -> "styling" to prevent React style prop conflicts
    const mapCommitType = (type: string | undefined): string => {
      if (type === "style") return "styling"
      return type || "unclassified"
    }

    // Chart 1: Classification (x-axis) stacked by Size
    // Group commits by commitType, then count by complexityClass
    const byClassification = new Map<string, Record<ComplexityClass, number>>()

    for (const commitType of commitTypeOrder) {
      byClassification.set(commitType, {
        XS: 0,
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
      })
    }

    // Also handle commits without a type (undefined)
    byClassification.set("unclassified", {
      XS: 0,
      S: 0,
      M: 0,
      L: 0,
      XL: 0,
    })

    for (const commit of commits) {
      const type = mapCommitType(commit.commitType)
      const size = commit.complexityClass
      const counts = byClassification.get(type)
      if (counts && size in counts) {
        counts[size] = (counts[size] || 0) + 1
      }
    }

    const classificationData = Array.from(byClassification.entries())
      .filter(([, counts]) => {
        // Only include types that have commits
        return Object.values(counts).some((v) => v > 0)
      })
      .map(([type, counts]) => ({
        classification: type,
        ...counts,
      }))

    // Chart 2: Size (x-axis) stacked by Classification
    // Group commits by complexityClass, then count by commitType
    const bySize = new Map<ComplexityClass, Record<string, number>>()

    for (const size of complexityOrder) {
      const typeCounts: Record<string, number> = {}
      for (const type of [...commitTypeOrder, "unclassified"]) {
        typeCounts[type] = 0
      }
      bySize.set(size, typeCounts)
    }

    for (const commit of commits) {
      const type = mapCommitType(commit.commitType)
      const size = commit.complexityClass
      const counts = bySize.get(size)
      if (counts) {
        counts[type] = (counts[type] || 0) + 1
      }
    }

    // Filter to only include types that have at least one commit across all sizes
    const activeTypes = new Set<string>()
    for (const [, counts] of bySize.entries()) {
      for (const [type, count] of Object.entries(counts)) {
        if (count > 0) {
          activeTypes.add(type)
        }
      }
    }

    const sizeData = complexityOrder.map((size) => {
      const counts = bySize.get(size)
      if (!counts) return { size }
      const entry: Record<string, string | number> = { size }
      for (const type of activeTypes) {
        entry[type] = counts[type] || 0
      }
      return entry
    })

    return {
      classificationData,
      sizeData,
      complexityOrder,
      activeTypes: Array.from(activeTypes),
    }
  }, [dataStore.allCommits])

  // Calculate weekly commit scores data for the Score tab
  const weeklyScoresData = useMemo(() => {
    const commits = dataStore.allCommits
    const now = new Date()
    const sixWeeksAgo = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000)

    // Filter commits from last 6 weeks
    const recentCommits = commits.filter((c) => c.timestampDate >= sixWeeksAgo)

    if (recentCommits.length === 0) {
      return { scoresBySize: [], scoresByClassification: [] }
    }

    // Get the earliest commit date to determine actual weeks available
    const earliestDate = new Date(Math.min(...recentCommits.map((c) => c.timestampDate.getTime())))
    const weeksAvailable = Math.min(
      6,
      Math.ceil((now.getTime() - earliestDate.getTime()) / (7 * 24 * 60 * 60 * 1000)),
    )

    // Helper function to get week label
    const getWeekLabel = (weekIndex: number, totalWeeks: number): string => {
      if (weekIndex === totalWeeks - 1) return "Current Week"
      if (weekIndex === totalWeeks - 2) return "Last Week"

      // Get the first day of the week (Monday)
      const weekStart = new Date(now.getTime() - (totalWeeks - weekIndex) * 7 * 24 * 60 * 60 * 1000)
      const monday = new Date(weekStart)
      monday.setDate(weekStart.getDate() - weekStart.getDay() + 1)

      const month = String(monday.getMonth() + 1).padStart(2, "0")
      const day = String(monday.getDate()).padStart(2, "0")
      return `${month}/${day}`
    }

    // Group commits by week
    const weeklyData = new Map<
      string,
      { bySize: Map<string, number>; byClass: Map<string, number> }
    >()

    for (let i = 0; i < weeksAvailable; i++) {
      const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekLabel = getWeekLabel(weeksAvailable - i - 1, weeksAvailable)

      const weekCommits = recentCommits.filter(
        (c) => c.timestampDate >= weekStart && c.timestampDate < weekEnd,
      )

      const bySize = new Map<string, number>()
      const byClass = new Map<string, number>()

      for (const commit of weekCommits) {
        const size = commit.complexityClass || "XS"
        const classification = commit.commitType || "other"

        bySize.set(size, (bySize.get(size) || 0) + commit.score)
        byClass.set(classification, (byClass.get(classification) || 0) + commit.score)
      }

      weeklyData.set(weekLabel, { bySize, byClass })
    }

    // Define the order
    const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
    const classifications = ["feature", "bugfix", "refactoring", "other"]

    // Build data for stacked bar chart by size
    const scoresBySize = Array.from(weeklyData.entries())
      .reverse()
      .map(([week, data]) => {
        const entry: Record<string, string | number> = { week }
        for (const size of complexityOrder) {
          entry[size] = data.bySize.get(size) || 0
        }
        return entry
      })

    // Build data for stacked bar chart by classification
    const scoresByClassification = Array.from(weeklyData.entries())
      .reverse()
      .map(([week, data]) => {
        const entry: Record<string, string | number> = { week }
        for (const classification of classifications) {
          entry[classification] = data.byClass.get(classification) || 0
        }
        return entry
      })

    return {
      scoresBySize,
      scoresByClassification,
      complexityOrder,
      classifications,
    }
  }, [dataStore.allCommits])

  // Calculate PR metrics for the new PR tab
  const prMetrics = useMemo(() => {
    const prs = dataStore.allPullRequests
    const now = new Date()
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

    // Current month PRs
    const currentMonthPRs = prs.filter(
      (pr) => pr.mergedAtDate && pr.mergedAtDate >= oneMonthAgo && pr.state === "merged",
    )

    // Previous month PRs (for comparison)
    const previousMonthPRs = prs.filter(
      (pr) =>
        pr.mergedAtDate &&
        pr.mergedAtDate >= twoMonthsAgo &&
        pr.mergedAtDate < oneMonthAgo &&
        pr.state === "merged",
    )

    // Calculate percentage change
    const currentCount = currentMonthPRs.length
    const previousCount = previousMonthPRs.length
    const percentageChange =
      previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : 0

    // Generate daily PR count for chart (last 30 days)
    const dailyPRs = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyPRs.set(dateKey, 0)
    }

    currentMonthPRs.forEach((pr) => {
      if (pr.mergedAtDate) {
        const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
        dailyPRs.set(dateKey, (dailyPRs.get(dateKey) || 0) + 1)
      }
    })

    const prChartData = Array.from(dailyPRs.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    // Calculate code changes (additions + deletions) for current month
    const dailyCodeChanges = new Map<string, { additions: number; deletions: number }>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyCodeChanges.set(dateKey, { additions: 0, deletions: 0 })
    }

    currentMonthPRs.forEach((pr) => {
      if (pr.mergedAtDate) {
        const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
        const current = dailyCodeChanges.get(dateKey) || {
          additions: 0,
          deletions: 0,
        }
        dailyCodeChanges.set(dateKey, {
          additions: current.additions + pr.additions,
          deletions: current.deletions + pr.deletions,
        })
      }
    })

    const codeChangesChartData = Array.from(dailyCodeChanges.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { additions, deletions }]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: additions + deletions,
      }))

    return {
      currentCount,
      previousCount,
      percentageChange,
      prChartData,
      codeChangesChartData,
      trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
        | "up"
        | "down"
        | "neutral",
    }
  }, [dataStore.allPullRequests])

  // Calculate weekly PR data for the PRs tab
  const weeklyPRData = useMemo(() => {
    const prs = dataStore.allPullRequests.filter((pr) => pr.state === "merged")
    const now = new Date()
    const sixWeeksAgo = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000)

    // Filter PRs from last 6 weeks
    const recentPRs = prs.filter((pr) => pr.mergedAtDate && pr.mergedAtDate >= sixWeeksAgo)

    if (recentPRs.length === 0) {
      return { prsByWeek: [], codeChangesByWeek: [] }
    }

    // Get the earliest PR date to determine actual weeks available
    const earliestDate = new Date(
      Math.min(
        ...recentPRs
          .filter((pr) => pr.mergedAtDate)
          .map((pr) => (pr.mergedAtDate as Date).getTime()),
      ),
    )
    const weeksAvailable = Math.min(
      6,
      Math.ceil((now.getTime() - earliestDate.getTime()) / (7 * 24 * 60 * 60 * 1000)),
    )

    // Helper function to get week label
    const getWeekLabel = (weekIndex: number, totalWeeks: number): string => {
      if (weekIndex === totalWeeks - 1) return "Current Week"
      if (weekIndex === totalWeeks - 2) return "Last Week"

      // Get the first day of the week (Monday)
      const weekStart = new Date(now.getTime() - (totalWeeks - weekIndex) * 7 * 24 * 60 * 60 * 1000)
      const monday = new Date(weekStart)
      monday.setDate(weekStart.getDate() - weekStart.getDay() + 1)

      const month = String(monday.getMonth() + 1).padStart(2, "0")
      const day = String(monday.getDate()).padStart(2, "0")
      return `${month}/${day}`
    }

    // Group PRs by week
    const weeklyData: {
      label: string
      count: number
      additions: number
      deletions: number
    }[] = []

    for (let i = 0; i < weeksAvailable; i++) {
      const weekStart = new Date(now.getTime() - (i + 1) * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekLabel = getWeekLabel(weeksAvailable - i - 1, weeksAvailable)

      const weekPRs = recentPRs.filter(
        (pr) => pr.mergedAtDate && pr.mergedAtDate >= weekStart && pr.mergedAtDate < weekEnd,
      )

      const additions = weekPRs.reduce((sum, pr) => sum + pr.additions, 0)
      const deletions = weekPRs.reduce((sum, pr) => sum + pr.deletions, 0)

      weeklyData.push({
        label: weekLabel,
        count: weekPRs.length,
        additions,
        deletions,
      })
    }

    // Reverse to show oldest first
    weeklyData.reverse()

    // Format for regular bar chart (PR count)
    const prsByWeek = weeklyData.map((week) => ({
      label: week.label,
      value: week.count,
    }))

    // Format for diverging bar chart (additions/deletions)
    const codeChangesByWeek = weeklyData.map((week) => ({
      label: week.label,
      positive: week.additions,
      negative: -week.deletions, // Negative for downward bars
    }))

    return { prsByWeek, codeChangesByWeek }
  }, [dataStore.allPullRequests])

  // Calculate Issue metrics for the new Issues tab
  const issueMetrics = useMemo(() => {
    const issues = dataStore.allIssues
    const now = new Date()
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

    // Current month issues created
    const currentMonthCreated = issues.filter((issue) => issue.createdAtDate >= oneMonthAgo)

    // Previous month issues created (for comparison)
    const previousMonthCreated = issues.filter(
      (issue) => issue.createdAtDate >= twoMonthsAgo && issue.createdAtDate < oneMonthAgo,
    )

    // Current month issues closed
    const currentMonthClosed = issues.filter(
      (issue) =>
        issue.closedAtDate && issue.closedAtDate >= oneMonthAgo && issue.state === "closed",
    )

    // Previous month issues closed (for comparison)
    const previousMonthClosed = issues.filter(
      (issue) =>
        issue.closedAtDate &&
        issue.closedAtDate >= twoMonthsAgo &&
        issue.closedAtDate < oneMonthAgo &&
        issue.state === "closed",
    )

    // Calculate percentage changes
    const createdCount = currentMonthCreated.length
    const previousCreatedCount = previousMonthCreated.length
    const createdPercentageChange =
      previousCreatedCount > 0
        ? ((createdCount - previousCreatedCount) / previousCreatedCount) * 100
        : 0

    const closedCount = currentMonthClosed.length
    const previousClosedCount = previousMonthClosed.length
    const closedPercentageChange =
      previousClosedCount > 0
        ? ((closedCount - previousClosedCount) / previousClosedCount) * 100
        : 0

    // Generate daily issue created count for chart (last 30 days)
    const dailyCreated = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyCreated.set(dateKey, 0)
    }

    currentMonthCreated.forEach((issue) => {
      const dateKey = issue.createdAtDate.toISOString().split("T")[0]
      dailyCreated.set(dateKey, (dailyCreated.get(dateKey) || 0) + 1)
    })

    const createdChartData = Array.from(dailyCreated.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    // Generate daily issue closed count for chart (last 30 days)
    const dailyClosed = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyClosed.set(dateKey, 0)
    }

    currentMonthClosed.forEach((issue) => {
      if (issue.closedAtDate) {
        const dateKey = issue.closedAtDate.toISOString().split("T")[0]
        dailyClosed.set(dateKey, (dailyClosed.get(dateKey) || 0) + 1)
      }
    })

    const closedChartData = Array.from(dailyClosed.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    return {
      createdCount,
      previousCreatedCount,
      createdPercentageChange,
      createdChartData,
      createdTrend: (createdPercentageChange > 0
        ? "up"
        : createdPercentageChange < 0
          ? "down"
          : "neutral") as "up" | "down" | "neutral",
      closedCount,
      previousClosedCount,
      closedPercentageChange,
      closedChartData,
      closedTrend: (closedPercentageChange > 0
        ? "up"
        : closedPercentageChange < 0
          ? "down"
          : "neutral") as "up" | "down" | "neutral",
    }
  }, [dataStore.allIssues])

  // Calculate releases metrics
  const releasesMetrics = useMemo(() => {
    const now = new Date()
    const twelveWeeksAgo = new Date(now.getTime() - 12 * 7 * 24 * 60 * 60 * 1000)
    const sixWeeksAgo = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000)

    // Filter releases for last 12 weeks (excluding drafts and prereleases)
    const recentReleases = dataStore.allReleases.filter(
      (release) => release.createdAtDate >= twelveWeeksAgo && !release.draft && !release.prerelease,
    )

    // Count total releases in last 12 weeks
    const totalReleases = recentReleases.length

    // Create weekly buckets for trend (last 12 weeks)
    const weeklyTrend: Array<{ label: string; value: number }> = []
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)

      const count = recentReleases.filter(
        (release) => release.createdAtDate >= weekStart && release.createdAtDate < weekEnd,
      ).length

      weeklyTrend.push({
        label: weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: count,
      })
    }

    // Calculate percentage change (comparing first 6 weeks vs last 6 weeks)
    const firstHalf = weeklyTrend.slice(0, 6).reduce((sum, week) => sum + week.value, 0)
    const secondHalf = weeklyTrend.slice(6, 12).reduce((sum, week) => sum + week.value, 0)
    const percentageChange = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0

    // Create stacked bar chart data for last 6 weeks by repository
    const last6WeeksReleases = dataStore.allReleases.filter(
      (release) => release.createdAtDate >= sixWeeksAgo && !release.draft && !release.prerelease,
    )

    // Get all unique repositories
    const repositories = Array.from(new Set(last6WeeksReleases.map((r) => r.repoName))).sort()

    // Create weekly buckets for stacked chart
    const stackedData: Array<Record<string, string | number>> = []
    for (let i = 5; i >= 0; i--) {
      const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)

      const weekData: Record<string, string | number> = {
        week: weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }

      // Count releases per repository for this week
      repositories.forEach((repo) => {
        const count = last6WeeksReleases.filter(
          (release) =>
            release.repoName === repo &&
            release.createdAtDate >= weekStart &&
            release.createdAtDate < weekEnd,
        ).length
        weekData[repo] = count
      })

      stackedData.push(weekData)
    }

    return {
      totalReleases,
      weeklyTrend,
      percentageChange,
      trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
        | "up"
        | "down"
        | "neutral",
      stackedData,
      repositories,
    }
  }, [dataStore.allReleases])

  // Calculate activity data for Activity Heatmap
  const activityData = useMemo(() => {
    const activityMap = new Map<string, number>()

    // Count commits per day
    dataStore.allCommits.forEach((commit) => {
      const dateKey = commit.timestampDate.toISOString().split("T")[0]
      activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
    })

    // Count PRs per day (use createdAtDate)
    dataStore.allPullRequests.forEach((pr) => {
      if (pr.createdAtDate) {
        const dateKey = pr.createdAtDate.toISOString().split("T")[0]
        activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
      }
    })

    // Count issues per day (use createdAtDate)
    dataStore.allIssues.forEach((issue) => {
      if (issue.createdAtDate) {
        const dateKey = issue.createdAtDate.toISOString().split("T")[0]
        activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1)
      }
    })

    // Convert to array format expected by ActivityHeatmap
    return Array.from(activityMap.entries()).map(([dateStr, count]) => ({
      date: new Date(dateStr),
      count,
    }))
  }, [dataStore.allCommits, dataStore.allPullRequests, dataStore.allIssues])

  // Helper function to format dates as relative time
  const formatRelativeDate = useCallback((date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSecs < 60) return "just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
    return `${Math.floor(diffDays / 365)}y ago`
  }, [])

  // Calculate recent activities for ActivityListCard (last 20 activities)
  const recentActivities = useMemo(() => {
    interface ActivityItem {
      id: string
      date: Date
      icon: React.ReactNode
      title: string
      description: string
      type: "commit" | "pr" | "issue" | "release"
    }

    const activities: ActivityItem[] = []

    // Add commits
    dataStore.allCommits.forEach((commit) => {
      activities.push({
        id: `commit-${commit.hash}`,
        date: commit.timestampDate,
        icon: <GitCommit className="h-4 w-4 text-blue-500" />,
        title: `${commit.authorHandle}: ${commit.message.slice(0, 60)}${commit.message.length > 60 ? "..." : ""}`,
        description: commit.repoName,
        type: "commit",
      })
    })

    // Add PRs
    dataStore.allPullRequests.forEach((pr) => {
      if (pr.createdAtDate) {
        activities.push({
          id: `pr-${pr.id}`,
          date: pr.createdAtDate,
          icon:
            pr.state === "merged" ? (
              <GitMerge key={`icon-pr-${pr.id}`} className="h-4 w-4 text-purple-500" />
            ) : (
              <GitPullRequest key={`icon-pr-${pr.id}`} className="h-4 w-4 text-green-500" />
            ),
          title: `${pr.creatorHandle}: ${pr.title}`,
          description: pr.repoName,
          type: "pr",
        })
      }
    })

    // Add issues
    dataStore.allIssues.forEach((issue) => {
      if (issue.createdAtDate) {
        activities.push({
          id: `issue-${issue.id}`,
          date: issue.createdAtDate,
          icon: <GitPullRequest className="h-4 w-4 text-orange-500" />,
          title: `${issue.creatorHandle}: ${issue.title}`,
          description: issue.repoName,
          type: "issue",
        })
      }
    })

    // Add releases
    dataStore.allReleases.forEach((release) => {
      if (release.createdAtDate) {
        activities.push({
          id: `release-${release.id}`,
          date: release.createdAtDate,
          icon: <Tag className="h-4 w-4 text-amber-500" />,
          title: `${release.authorHandle}: Released ${release.tagName}${release.name ? ` - ${release.name}` : ""}`,
          description: release.repoName,
          type: "release",
        })
      }
    })

    // Sort by date descending and take the most recent 20
    const sorted = activities.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 20)

    // Format for ActivityListCard
    return sorted.map((activity) => ({
      id: activity.id,
      icon: activity.icon,
      title: activity.title,
      description: activity.description,
      date: formatRelativeDate(activity.date),
    }))
  }, [
    dataStore.allCommits,
    dataStore.allPullRequests,
    dataStore.allIssues,
    dataStore.allReleases,
    formatRelativeDate,
  ])

  // Create tabs
  const tabs: TabDefinition[] = [
    {
      id: "insights",
      label: i18nStore.t("tabPerformance"),
      content: (
        <InsightDashboard
          store={dashboardStore}
          tableTitle={i18nStore.t("chartDetails")}
          dataTableTranslations={{
            filtersButton: i18nStore.t("dataTableFilters"),
            filterBy: i18nStore.t("dataTableFilterBy"),
            noResults: i18nStore.t("dataTableNoResults"),
            rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
            page: i18nStore.t("dataTablePage"),
            of: i18nStore.t("dataTableOf"),
            totalRows: i18nStore.t("dataTableTotalRows"),
          }}
          searchPlaceholder={i18nStore.t("dataTableSearch")}
        />
      ),
    },
    {
      id: "commits",
      label: i18nStore.t("tabScore"),
      content: (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <StackedBarChart
              title={i18nStore.t("chartCommitsByClassification")}
              description={i18nStore.t("chartCommitsByClassificationDesc")}
              data={barChartData.classificationData}
              categoryKey="classification"
              stackKeys={barChartData.complexityOrder}
            />
            <StackedBarChart
              title={i18nStore.t("chartCommitsBySize")}
              description={i18nStore.t("chartCommitsBySizeDesc")}
              data={barChartData.sizeData}
              categoryKey="size"
              stackKeys={barChartData.activeTypes}
            />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <StackedBarChart
              title={i18nStore.t("chartCommitScoresByWeek")}
              description={i18nStore.t("chartCommitScoresByWeekDesc")}
              data={weeklyScoresData.scoresBySize}
              categoryKey="week"
              stackKeys={weeklyScoresData.complexityOrder || []}
            />
            <StackedBarChart
              title={i18nStore.t("chartCommitScoresByClassificationWeek")}
              description={i18nStore.t("chartCommitScoresByClassificationWeekDesc")}
              data={weeklyScoresData.scoresByClassification}
              categoryKey="week"
              stackKeys={weeklyScoresData.classifications || []}
            />
          </div>
        </div>
      ),
    },
    {
      id: "prs",
      label: i18nStore.t("tabPRs"),
      content: (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <MetricsCard
              title={i18nStore.t("chartPRsMerged")}
              description={i18nStore.tf("chartPRsMergedDesc", {
                count: prMetrics.currentCount.toString(),
                prevCount: prMetrics.previousCount.toString(),
              })}
              percentage={Math.abs(prMetrics.percentageChange)}
              trend={prMetrics.trend}
              chartData={prMetrics.prChartData}
              chartType="bar"
            />
            <MetricsCard
              title={i18nStore.t("chartCodeChanges")}
              description={i18nStore.t("chartCodeChangesDesc")}
              percentage={0}
              trend="neutral"
              chartData={prMetrics.codeChangesChartData}
              chartType="line"
            />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <SimpleBarChart
              title={i18nStore.t("chartPRsMergedByWeek")}
              description={i18nStore.t("chartPRsMergedByWeekDesc")}
              data={weeklyPRData.prsByWeek}
            />
            <DivergingBarChart
              title={i18nStore.t("chartCodeChangesByWeek")}
              description={i18nStore.t("chartCodeChangesByWeekDesc")}
              data={weeklyPRData.codeChangesByWeek}
              positiveLabel={i18nStore.t("linesAdded")}
              negativeLabel={i18nStore.t("linesRemoved")}
            />
          </div>
        </div>
      ),
    },
    {
      id: "issues",
      label: i18nStore.t("tabIssues"),
      content: (
        <div className="grid gap-6 md:grid-cols-2">
          <MetricsCard
            title={i18nStore.t("chartIssuesCreated")}
            description={i18nStore.tf("chartIssuesCreatedDesc", {
              count: issueMetrics.createdCount.toString(),
              prevCount: issueMetrics.previousCreatedCount.toString(),
            })}
            percentage={Math.abs(issueMetrics.createdPercentageChange)}
            trend={issueMetrics.createdTrend}
            chartData={issueMetrics.createdChartData}
            chartType="bar"
          />
          <MetricsCard
            title={i18nStore.t("chartIssuesClosed")}
            description={i18nStore.tf("chartIssuesClosedDesc", {
              count: issueMetrics.closedCount.toString(),
              prevCount: issueMetrics.previousClosedCount.toString(),
            })}
            percentage={Math.abs(issueMetrics.closedPercentageChange)}
            trend={issueMetrics.closedTrend}
            chartData={issueMetrics.closedChartData}
            chartType="bar"
          />
        </div>
      ),
    },
    {
      id: "releases",
      label: i18nStore.t("tabReleases"),
      content: (
        <div className="grid gap-6">
          {/* Releases Trend Card */}
          <MetricsCard
            title={i18nStore.t("releasesTitle")}
            description={i18nStore.t("releasesDescription")}
            percentage={Math.abs(releasesMetrics.percentageChange)}
            trend={releasesMetrics.trend}
            chartData={releasesMetrics.weeklyTrend}
            chartType="line"
            trendLabel={i18nStore.t("chartTrend")}
          />

          {/* Stacked Bar Chart - Releases by Repository */}
          <StackedBarChart
            title={i18nStore.t("releasesChartTitle")}
            description={i18nStore.t("releasesChartDescription")}
            data={releasesMetrics.stackedData}
            categoryKey="week"
            stackKeys={releasesMetrics.repositories}
          />
        </div>
      ),
    },
    {
      id: "activity",
      label: i18nStore.t("tabActivity"),
      content: (
        <div className="space-y-6">
          <ActivityHeatmap
            title={i18nStore.t("chartActivityTitle")}
            description={i18nStore.t("chartActivityDescription")}
            data={activityData}
          />
          <ActivityListCard title={i18nStore.t("recentActivity")} activities={recentActivities} />
        </div>
      ),
    },
  ]

  // Log dashboard render
  logger.debug("Rendering OrganizationDashboard", {
    component: "OrganizationDashboard",
    commits: dataStore.allCommits.length,
    prs: dataStore.allPullRequests.length,
  })

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("dashboardOrganizationTitle")}
          subtitle={i18nStore.t("dashboardOrganizationSubtitle")}
        >
          <TabsWithStore tabs={tabs} defaultSelectedTab="insights" />
        </PageHeader>
      </div>
    </div>
  )
})
